/* eslint-disable enforce-catch-error-handled/require-catch-error-handling */
// TODO: Remove this eslint-disable comment in a separate PR (RCP-27576)
import {
  CreateSLARequestDto,
  CreateSLAResponseDto,
} from '@issuemanager/api-client';
import {
  ControlledNumberField,
  ControlledTextField,
  ExtendedControlledAutocomplete,
} from '@issuemanager/apps/client/components';
import {
  useBadRequestOrConflictError,
  useIssueTypeIssueSubtypeInfiniteScroll,
} from '@issuemanager/apps/client/hooks';
import {
  formHasErrorsToDisplay,
  getFormStateErrors,
  joinFormErrorsWithServiceErrors,
  mapFormLabelsToFieldLabel,
} from '@issuemanager/apps/client/utils';
import { useIssueTemplateByIssueTypeAndIssueSubtype } from '@issuemanager/client/features/issue-templates';
import {
  useIssueSeverities,
  useLocalizedIssueTypes,
} from '@issuemanager/client/features/issues';
import { useCreateSLA, useUpdateSLA } from '@issuemanager/client/features/slas';
import { ISSUE_SUBTYPE_NONE_VALUE, IssueType } from '@issuemanager/constants';
import { useEventCallback } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { ReactNode, useEffect, useMemo } from 'react';
import { RegisterOptions, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type UseCreateSLAFormProps = {
  onSuccess?: (values: CreateSLAResponseDto) => void;
  open?: boolean;
  id?: string;
};

type LocalFields =
  | 'issueType'
  | 'issueSubtype'
  | 'severity'
  | 'slaDays'
  | 'maxDueDate'
  | 'escalationDays'
  | 'reminderDays';
type RulesFields = LocalFields;

type NonIterableFields = Exclude<LocalFields, 'reminderDays'>;
type ReminderDaysValueType = { value: number };

export type SLAFormValues = Omit<
  CreateSLARequestDto,
  'reminderDays' | 'severity' | 'issueTypeId'
> & {
  reminderDays: ReminderDaysValueType[];
  issueTypeId: CreateSLARequestDto['issueTypeId'] | null;
  severity?: CreateSLARequestDto['severity'] | null;
};
const REMINDER_DAYS_DEFAULT_VALUE: ReminderDaysValueType = { value: 1 };

const DEFAULT_VALUES: SLAFormValues = {
  reminderDays: [],
  issueTypeId: null,
  issueSubtypeId: undefined,
  escalationDays: 1,
  maxDueDate: 1,
  slaDays: 1,
  severity: null,
};

export function useSLAForm(props: UseCreateSLAFormProps) {
  const { id, open, onSuccess } = props;

  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation(['settings', 'common']);

  const { errors: serviceErrors, cleanErrors: cleanServiceErrors } =
    useBadRequestOrConflictError();

  const createSLA = useCreateSLA({
    onSuccess: (data) => {
      onSuccess?.(data);
      enqueueSnackbar(t('settings:slaConfiguration.sidePanel.created'), {
        variant: 'success',
      });
    },
  });

  const updateSLA = useUpdateSLA({
    onSuccess: (data) => {
      onSuccess?.(data);
      enqueueSnackbar(t('settings:slaConfiguration.sidePanel.edited'), {
        variant: 'success',
      });
    },
  });

  const form = useForm<SLAFormValues>({
    mode: 'onSubmit',
  });

  const reminderDaysFieldArray = useFieldArray({
    control: form.control,
    name: 'reminderDays',
  });

  const selectedIssueType = form.watch('issueTypeId');
  const selectedIssueSubtype = form.watch('issueSubtypeId');

  const handleDelete = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    index: number,
  ) => {
    e.stopPropagation();
    reminderDaysFieldArray.remove(index);
  };

  const handleAddReminderDays = () => {
    reminderDaysFieldArray.append(REMINDER_DAYS_DEFAULT_VALUE);
  };

  const {
    issueTypeOptions,
    issueTypesQuery,
    subtypesOptions,
    handleInputChange,
    issueSubtypesQuery,
  } = useIssueTypeIssueSubtypeInfiniteScroll({
    selectedIssueType,
    issueTypeQueryParams: {
      filterByIssueTypeManager: true,
      handleError: true,
    },
    issueSubtypeQueryParams: {
      filterByIssueTypeManager: true,
      handleError: true,
    },
    enabled: !!open,
  });

  const issueTemplate = useIssueTemplateByIssueTypeAndIssueSubtype({
    issueTypeId: selectedIssueType ?? '',
    issueSubtypeId:
      selectedIssueSubtype === ISSUE_SUBTYPE_NONE_VALUE
        ? undefined
        : selectedIssueSubtype ?? undefined,
    enabled: !!selectedIssueType,
  });

  const severities = useIssueSeverities(false);
  const localizedIssueTypes = useLocalizedIssueTypes();
  const severitiesMapped = useMemo(
    () =>
      severities.map((severity) => ({
        value: severity.value as string,
        label: severity.label,
      })),
    [severities],
  );

  const resetForm = () => {
    if (id) {
      form.reset({
        slaDays: DEFAULT_VALUES.slaDays,
        maxDueDate: DEFAULT_VALUES.maxDueDate,
        escalationDays: DEFAULT_VALUES.escalationDays,
        reminderDays: DEFAULT_VALUES.reminderDays,
        issueTypeId: form.getValues('issueTypeId'),
        issueSubtypeId: form.getValues('issueSubtypeId'),
        severity: form.getValues('severity'),
      });
    } else {
      form.reset(DEFAULT_VALUES);
    }
  };

  useEffect(() => {
    if (!open) {
      form.reset(DEFAULT_VALUES);
    }
  }, [open]);

  const cleanErrors = () => {
    form.clearErrors();
    cleanServiceErrors();
  };

  const onSubmit = useEventCallback(
    form.handleSubmit(async (values) => {
      try {
        const reminderDays = values.reminderDays.map((item) => item.value);
        if (id) {
          await updateSLA.mutateAsync({
            id,
            escalationDays: values.escalationDays,
            maxDueDate: values.maxDueDate,
            slaDays: values.slaDays,
            reminderDays,
          });
        } else {
          await createSLA.mutateAsync({
            ...values,
            reminderDays,
            issueTypeId: values.issueTypeId!,
            issueSubtypeId:
              values.issueSubtypeId === ISSUE_SUBTYPE_NONE_VALUE
                ? undefined
                : values.issueSubtypeId,
            severity: values.severity!,
          });
        }
      } catch (e) {
        console.error(e);
      }
    }),
  );

  const rules: Record<RulesFields, RegisterOptions<SLAFormValues>> = {
    issueType: {
      required: t(
        'settings:slaConfiguration.form.errorMessages.issueType.required',
      ),
    },
    issueSubtype: {
      required: t(
        'settings:slaConfiguration.form.errorMessages.issueSubtype.required',
      ),
    },
    severity: {
      required: t(
        'settings:slaConfiguration.form.errorMessages.severity.required',
      ),
    },
    slaDays: {
      required: t(
        'settings:slaConfiguration.form.errorMessages.slaDays.required',
      ),
      min: {
        message: t('settings:slaConfiguration.form.errorMessages.slaDays.min'),
        value: 1,
      },
    },
    maxDueDate: {
      required: t(
        'settings:slaConfiguration.form.errorMessages.maxDueDate.required',
      ),
      min: {
        message: t(
          'settings:slaConfiguration.form.errorMessages.maxDueDate.min',
        ),
        value: 1,
      },
      validate: {
        largerThanSLADays: (value, formValues) =>
          Number(value) <= Number(formValues.slaDays) ||
          t(
            'settings:slaConfiguration.form.errorMessages.maxDueDate.largerThanSLADays',
          ),
        greaterThanOrEqualToIssueTemplateDueDate: (value) => {
          if (!value || !issueTemplate.data || !issueTemplate.data.dueDate) {
            return true;
          }
          return (
            Number(value) >= issueTemplate.data.dueDate ||
            t(
              'settings:slaConfiguration.form.errorMessages.maxDueDate.greaterThanOrEqualToIssueTemplateDueDate',
            )
          );
        },
      },
    },
    escalationDays: {
      required: t(
        'settings:slaConfiguration.form.errorMessages.escalationDays.required',
      ),
      min: {
        message: t(
          'settings:slaConfiguration.form.errorMessages.escalationDays.min',
        ),
        value: 1,
      },
      validate: (value, formValues) =>
        Number(value) < Number(formValues.maxDueDate) ||
        t(
          'settings:slaConfiguration.form.errorMessages.escalationDays.largerThanMaxDueDate',
        ),
    },
    reminderDays: {
      min: {
        message: t(
          'settings:slaConfiguration.form.errorMessages.reminderDays.min',
        ),
        value: 1,
      },
    },
  };

  const labels = {
    issueTypeId: t('settings:slaConfiguration.form.labels.issueType'),
    issueSubtypeId: t('settings:slaConfiguration.form.labels.issueSubtype'),
    severity: t('settings:slaConfiguration.form.labels.severity'),
    slaDays: t('settings:slaConfiguration.form.labels.slaDays'),
    maxDueDate: t('settings:slaConfiguration.form.labels.maxDueDate'),
    escalationDays: t('settings:slaConfiguration.form.labels.escalationDays'),
    reminderDays: t('settings:slaConfiguration.form.labels.reminderDays'),
  };

  const labelsInfo = {
    slaDays: t('settings:slaConfiguration.form.labelsInfo.slaDays'),
    maxDueDate: t('settings:slaConfiguration.form.labelsInfo.maxDueDate'),
    escalationDays: t(
      'settings:slaConfiguration.form.labelsInfo.escalationDays',
    ),
    reminderDays: t('settings:slaConfiguration.form.labelsInfo.reminderDays'),
  };

  const fields: Record<NonIterableFields, ReactNode> = {
    issueType: (
      <ControlledTextField
        name='issueTypeId'
        label={labels.issueTypeId}
        data-testid='issueTypeId'
        control={form.control}
        rules={rules.issueType}
        disabled={Boolean(id)}
        options={issueTypeOptions.map((issueType) => ({
          value: issueType.id,
          label:
            localizedIssueTypes[issueType.name as IssueType] ?? issueType.name,
        }))}
        onChange={() => {
          form.setValue('issueSubtypeId', undefined);
        }}
        placeholder={t('common:inputs.listBox.empty')}
      />
    ),
    issueSubtype: (
      <ExtendedControlledAutocomplete
        name='issueSubtypeId'
        label={labels.issueSubtypeId}
        control={form.control}
        rules={rules.issueSubtype}
        loading={issueSubtypesQuery.isFetching}
        disabled={Boolean(id)}
        options={[
          { id: ISSUE_SUBTYPE_NONE_VALUE, label: t('common:subtypes.none') },
          ...subtypesOptions.map((issueSubtype) => ({
            id: issueSubtype.id,
            label: issueSubtype.name,
          })),
        ]}
        onTextChange={(value, reason) => {
          handleInputChange(value, 'issueSubtype', reason);
        }}
        placeholder={t('common:inputs.listBox.empty')}
      />
    ),
    severity: (
      <ControlledTextField
        name='severity'
        rules={rules.severity}
        label={labels.severity}
        control={form.control}
        disabled={Boolean(id)}
        options={severitiesMapped}
      />
    ),
    slaDays: (
      <ControlledNumberField
        name='slaDays'
        label={labels.slaDays}
        labelInfo={labelsInfo.slaDays}
        control={form.control}
        rules={rules.slaDays}
        defaultValue={1}
      />
    ),
    maxDueDate: (
      <ControlledNumberField
        name='maxDueDate'
        label={labels.maxDueDate}
        labelInfo={labelsInfo.maxDueDate}
        control={form.control}
        rules={rules.maxDueDate}
        defaultValue={1}
      />
    ),
    escalationDays: (
      <ControlledNumberField
        name='escalationDays'
        label={labels.escalationDays}
        labelInfo={labelsInfo.escalationDays}
        control={form.control}
        rules={rules.escalationDays}
        defaultValue={1}
      />
    ),
  };

  let labelsObj = { ...labels };
  const reminderDaysFields = reminderDaysFieldArray.fields.map(
    (field, index) => {
      labelsObj = {
        ...labelsObj,
        [`reminderDays.${index}.value`]: labels.reminderDays,
      };

      return {
        id: field.id,
        handleDelete: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
          handleDelete(e, index),
        reminderDays: (
          <ControlledNumberField
            name={`reminderDays.${index}.value`}
            label={labels.reminderDays}
            labelInfo={labelsInfo.reminderDays}
            control={form.control}
            rules={rules.reminderDays}
            defaultValue={REMINDER_DAYS_DEFAULT_VALUE.value}
          />
        ),
      };
    },
  );

  const formStateErrors = getFormStateErrors<SLAFormValues>(
    mapFormLabelsToFieldLabel(labelsObj),
    rules,
    form.formState.errors,
  );
  const errors = joinFormErrorsWithServiceErrors(
    formStateErrors,
    serviceErrors,
  );

  const hasErrors = formHasErrorsToDisplay(errors);

  return {
    form,
    onSubmit,
    fields,
    reminderDaysFields,
    handleAddReminderDays,
    isLoading: createSLA.isPending || updateSLA.isPending,
    errors,
    hasErrors,
    cleanErrors,
    resetForm,
    issueTypesQuery,
  };
}
